/**
 * Hook dữ liệu của màn Kết nối nguồn. Danh sách nguồn đọc chung `useDanhSachNguon` (`useChung`); mọi lệnh ghi ở đây
 * vô hiệu hoá nhánh `KHOA.nguon`, nên lưới nguồn ở Kho dữ liệu, Truy vấn mẫu và ô chọn nguồn ở Hỏi đáp cùng làm mới.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';
import type { CreateSourcePayload } from '../types';
import { KHOA, thuLai } from './useChung';

function useLamMoiNguon() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KHOA.nguon });
}

export function useKiemNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation({ mutationFn: (id: string) => khoTroLy.kiemNguon(id), onSuccess: () => void lamMoi() });
}

export function useDungKho() {
  const lamMoi = useLamMoiNguon();
  return useMutation({ mutationFn: (id: string) => khoTroLy.dungKho(id), onSuccess: () => void lamMoi() });
}

export function useDongBoNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation({ mutationFn: (id: string) => khoTroLy.dongBo(id), onSuccess: () => void lamMoi() });
}

/** Sửa một phần nguồn (`PATCH`) — tự đồng bộ, nhịp, mô tả, cách đọc, kết nối API, ghi chú nghiệp vụ. */
export function useSuaNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: ({ id, du }: { id: string; du: Record<string, unknown> }) => khoTroLy.suaNguon(id, du),
    onSuccess: () => void lamMoi(),
  });
}

export function useTaoNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: (du: CreateSourcePayload) => khoTroLy.taoNguon(du),
    onSuccess: () => void lamMoi(),
  });
}

/** `drop` xoá luôn kho bản sao — phá huỷ, màn hình hỏi lại trước. */
export function useXoaNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation({
    mutationFn: ({ id, drop }: { id: string; drop: boolean }) => khoTroLy.xoaNguon(id, drop),
    onSuccess: () => void lamMoi(),
  });
}

/** Dọn kho bản sao cũ của nguồn đã chuyển sang đọc trực tiếp. */
export function useXoaKhoCu() {
  const lamMoi = useLamMoiNguon();
  return useMutation({ mutationFn: (id: string) => khoTroLy.xoaKhoBanSao(id), onSuccess: () => void lamMoi() });
}

/**
 * Bảng đã khai của một nguồn, cho khai nhanh theo mẫu (nhận ra SAP B1 qua tên bảng). Gọi theo yêu cầu bằng
 * `queryClient.fetchQuery` chứ không tải sẵn cho mọi thẻ.
 */
export function useLayBangNguon() {
  const qc = useQueryClient();
  return (id: string) =>
    qc.fetchQuery({
      queryKey: KHOA.trangThai(id),
      queryFn: () => khoTroLy.trangThai(id),
      staleTime: 10_000,
    });
}

/** Danh sách bảng đã khai — sơ đồ lược đồ. Chỉ tải khi hộp thoại mở. */
export function useBangDaKhai(id: string, bat: boolean) {
  return useQuery({
    queryKey: [...KHOA.nguonChiTiet(id), 'bang'],
    queryFn: () => khoTroLy.bangDaKhai(id),
    enabled: bat && Boolean(id),
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Trạng thái bản sao (bảng, số dòng, mốc đồng bộ). Kho chưa dựng thì `/status` là 404 — bên gọi chỉ bật khi `ready`. */
export function useTrangThaiBanSao(id: string, bat: boolean) {
  return useQuery({
    queryKey: KHOA.trangThai(id),
    queryFn: () => khoTroLy.trangThai(id),
    enabled: bat && Boolean(id),
    staleTime: 10_000,
    retry: thuLai,
  });
}
